// src/servicesheet.js

// Ensure these match your .env file settings or are hardcoded if necessary (not recommended for password)
const SCRIPT_URL = process.env.REACT_APP_SCRIPT_URL || "YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL";
const SHEET_PASSWORD = process.env.REACT_APP_SHEET_PASSWORD || "YOUR_SHEET_PASSWORD";

/**
 * Fetches all necessary trading data from the Google Apps Script.
 * Processes trade history to filter out non-trading activities and use net profit.
 * @param {string} periodFilter - The period for which to filter trades (e.g., '7d', '30d', 'all').
 * @returns {Promise<Object>} A promise that resolves to an object containing all fetched and processed data.
 */
export const fetchTradingDataFromSheets = async (periodFilter = 'all') => {
    if (!SCRIPT_URL || SCRIPT_URL === "YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL" || SCRIPT_URL.startsWith("YOUR_APPS_SCRIPT_WEB_APP_URL")) {
        const errorMsg = "Error: Google Apps Script URL is not configured correctly in servicesheet.js or your .env file.";
        console.error(errorMsg);
        throw new Error(errorMsg);
    }

    const params = new URLSearchParams({
        action: "get_all_dashboard_data", // This action needs to be handled by your Apps Script doGet
        password: SHEET_PASSWORD,
        period: periodFilter, // Pass the period filter to Apps Script
    });

    const fullUrl = `${SCRIPT_URL}?${params.toString()}`;
    let responseData;

    try {
        // Avoid logging password in production URLs
        const logUrl = fullUrl.replace(/password=[^&]+&?/, 'password=REDACTED&').replace(/password=[^&]+$/, 'password=REDACTED');
        console.log(`Workspaceing dashboard data from Google Sheets: ${logUrl}`);
        
        const response = await fetch(fullUrl, {
            method: "GET",
            mode: "cors",
            redirect: "follow",
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error("Error fetching data from Google Sheets:", response.status, errorText);
            throw new Error(`Network error: ${response.status}. Could not retrieve data. Server said: ${errorText.substring(0, 200)}`);
        }

        responseData = await response.json();
        if (responseData.status !== "success") {
            console.error("Error reported by Google Apps Script:", responseData.message);
            throw new Error(responseData.message || "An error occurred on the server.");
        }
        console.log("Data successfully fetched and parsed from Google Sheets.");

    } catch (error) {
        console.error("Failed to fetch or parse data from Google Sheets:", error);
        throw new Error(`Data retrieval failed. Please check your connection or Apps Script setup. (${error.message})`);
    }

    if (!responseData || typeof responseData !== 'object') {
        console.error("Invalid data structure received:", responseData);
        throw new Error("Received invalid data structure from Google Sheets.");
    }

    // --- Process Trade History ---
    if (responseData.tradeHistory && Array.isArray(responseData.tradeHistory)) {
        const processedTradeHistory = responseData.tradeHistory
            .filter(trade => {
                const symbol = String(trade.symbol || '').toLowerCase().trim();
                const type = String(trade.type || '').toLowerCase().trim(); // Type from MT5 (e.g., "Buy", "Sell", "Balance")

                if (symbol === "balance") return false;
                const nonTradeTypes = ["balance", "credit", "charge", "correction", "bonus"];
                if (nonTradeTypes.includes(type)) return false;
                
                // Ensure it's a trade with essential fields
                return trade.ticket && trade.time;
            })
            .map(trade => {
                const grossProfit = parseFloat(trade.profit) || 0;
                const commission = parseFloat(trade.commission) || 0;
                const swap = parseFloat(trade.swap) || 0;
                // IMPORTANT: Use the "total" field from Apps Script as netProfit
                // Fallback to calculating if "total" is somehow missing
                const netProfit = trade.total !== undefined ? parseFloat(trade.total) : (grossProfit + commission + swap);

                let tradeDateStr = '';
                let tradeTimeStr = '00:00:00';
                if (trade.time) { // Assuming trade.time is a full datetime string/timestamp from Apps Script
                    try {
                        const d = new Date(trade.time); // Attempt to parse
                        if (!isNaN(d.getTime())) {
                            tradeDateStr = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
                            tradeTimeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
                        } else {
                             console.warn(`Could not parse date from trade.time: ${trade.time} for ticket ${trade.ticket}`);
                        }
                    } catch (e) {
                        console.warn(`Error parsing date from trade.time: ${trade.time} for ticket ${trade.ticket}`, e);
                    }
                }

                return {
                    ticket: String(trade.ticket),
                    id: String(trade.id || trade.ticket), // For journal key consistency
                    date: tradeDateStr, // Expected by React components
                    time: tradeTimeStr, // Expected by React components
                    originalTime: trade.time, // Keep original if needed elsewhere
                    symbol: String(trade.symbol || ''),
                    type: String(trade.type || ''),     // e.g., "Buy", "Sell"
                    volume: parseFloat(trade.volume) || 0,
                    price: parseFloat(trade.price) || 0, // Entry price
                    sl: parseFloat(trade.sl) || 0,
                    tp: parseFloat(trade.tp) || 0,
                    profit: grossProfit,          // Store gross profit for reference if needed
                    commission: commission,
                    swap: swap,
                    netProfit: netProfit,         // **This is the key field for calculations**
                    pips: parseFloat(trade.pips) || 0,
                    comment: String(trade.comment || ''), // Original MT5 comment if sent
                    // Journal fields from sheet (handle potential non-existence gracefully)
                    notes: String(trade.notes || trade.comment || ''), // Use comment as fallback for notes
                    strategy: String(trade.strategy || ''),
                    reasonEntry: String(trade.reasonEntry || ''),
                    reasonExit: String(trade.reasonExit || ''),
                    lessonsLearned: String(trade.lessonsLearned || ''),
                };
            });
        responseData.tradeHistory = processedTradeHistory;
        console.log(`Processed ${processedTradeHistory.length} trades after filtering balance ops.`);
    } else {
        responseData.tradeHistory = []; // Ensure it's an array
        console.warn("Trade history was missing or not an array in the response.");
    }

    // --- Sanitize other expected data parts ---
    const ensureObject = (data) => (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
    const ensureArray = (data) => (data && Array.isArray(data)) ? data : [];

    responseData.accountInfo = ensureObject(responseData.accountInfo);
    responseData.statistics = ensureObject(responseData.statistics);
    responseData.openPositions = ensureArray(responseData.openPositions).map(pos => ({
        ...pos,
        ticket: String(pos.ticket || ''),
        profit: parseFloat(pos.profit) || 0,
        currentPrice: parseFloat(pos.currentPrice || pos.current_price) || 0, // Handle key variations
        volume: parseFloat(pos.volume) || 0,
        price: parseFloat(pos.price) || 0,
        sl: parseFloat(pos.sl) || 0,
        tp: parseFloat(pos.tp) || 0,
        swap: parseFloat(pos.swap) || 0,
        total: (parseFloat(pos.profit) || 0) + (parseFloat(pos.swap) || 0), // Calculate total for open pos
    }));
    responseData.symbolPerformance = ensureArray(responseData.symbolPerformance);
    responseData.timePerformance = ensureArray(responseData.timePerformance);

    // Ensure numeric values in accountInfo and statistics are numbers
    if(responseData.accountInfo) {
        Object.keys(responseData.accountInfo).forEach(key => {
            if (['balance', 'equity', 'profit', 'margin', 'freeMargin', 'marginLevel', 'leverage'].includes(key)) {
                responseData.accountInfo[key] = parseFloat(responseData.accountInfo[key]) || 0;
            }
        });
    }
    if(responseData.statistics) {
         Object.keys(responseData.statistics).forEach(key => {
            // Assuming most statistics are numbers. Add specific parsing if some are strings.
            if (key !== 'lastUpdated' && key !== 'someStringStat') { // Example: don't parse date strings
                 responseData.statistics[key] = parseFloat(responseData.statistics[key]) || 0;
            }
        });
    }

    return responseData;
};